/**
 * Download Manager Modal
 *
 * Custom file name editor and format converter for batch downloads.
 * Features:
 * - Per-file custom name editing
 * - Batch prefix/suffix controls
 * - Format conversion (MP3, WAV, FLAC, AAC, OGG)
 * - Bitrate, sample rate, bit depth options
 * - Preset save/load per org
 * - JSZip batch download with progress
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
  Table,
  Progress,
} from 'reactstrap';
import JSZip from 'jszip';
import { doc as fbDoc, getDoc as fbGetDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { FirebaseContext } from '../Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { parseFlightingCode } from '../../utils/flightingCode';

const QC_SERVICE_URL = process.env.REACT_APP_QC_SERVICE_URL || 'http://localhost:8080';

const FORMAT_OPTIONS = [
  { value: 'original', label: 'Original (no conversion)' },
  { value: 'mp3', label: 'MP3' },
  { value: 'mp2', label: 'MP2 (broadcast)' },
  { value: 'wav', label: 'WAV' },
  { value: 'flac', label: 'FLAC' },
  { value: 'aac', label: 'AAC' },
  { value: 'ogg', label: 'OGG' },
];

// MP3/AAC/OGG cap at 320 kbps. MP2 is a broadcast format and 384 kbps
// (EBU Tech 3285 / MUSICAM) is the standard; we expose it only when MP2
// is selected so it doesn't show up as a spec-out-of-range option for
// the other lossy codecs.
const BITRATE_OPTIONS_DEFAULT = ['128k', '192k', '256k', '320k'];
const BITRATE_OPTIONS_MP2 = ['128k', '192k', '256k', '320k', '384k'];
const SAMPLE_RATE_OPTIONS = ['', '44100', '48000'];
const BIT_DEPTH_OPTIONS = ['', '16', '24', '32'];
const CHANNEL_OPTIONS = ['', '1', '2'];

interface DownloadFile {
  id: string;
  originalName: string;
  customName: string;
  downloadUrl: string;
  fileType: 'mp3' | 'wav';
  trackName?: string;
  station?: string;
}

interface Preset {
  id?: string;
  name: string;
  prefix: string;
  suffix: string;
  format: string;
  bitrate: string;
  sampleRate: string;
  bitDepth: string;
  channels: string;
  // Time-stretch: pin output to the duration encoded in the filename's
  // flighting code (e.g. "_30_" → 30s) when the source differs by more
  // than `timeStretchGraceMs` milliseconds. Pitch is preserved via ffmpeg
  // atempo. Disabled by default for backwards compatibility with existing
  // presets that don't carry these fields.
  timeStretchEnabled?: boolean;
  timeStretchGraceMs?: number;
}

const DEFAULT_GRACE_MS = 50;

interface DownloadManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  files: DownloadFile[];
  zipName?: string;
  /** OPTIONAL — when present, the modal reports download completion
   *  back to the source studio's delivery records via the
   *  `stampDeliveryDownload` Cloud Function. Enables the studio's
   *  Jobs list + JobDetail per-delivery badge to show "3/8 downloaded"
   *  and per-recipient "Downloaded"/"Downloaded — partial" pills.
   *  Only meaningful when the current user is the broadcaster
   *  recipient — the CF verifies membership. Callers on the studio
   *  side (previewing their own campaign audio) should omit. */
  deliveryContext?: {
    sourceOrgId: string;
    jobId: string;
    broadcasterOrgId: string;
  };
}

const CONCURRENCY_LIMIT = 3;

async function fetchWithConcurrencyLimit(
  tasks: (() => Promise<any>)[],
  limit: number,
  onProgress?: (done: number) => void
) {
  let i = 0;
  const results = new Array(tasks.length);
  const queue: Promise<void>[] = [];

  function runNext(): Promise<void> {
    if (i >= tasks.length) return Promise.resolve();
    const idx = i++;
    return tasks[idx]().then(result => {
      results[idx] = result;
      if (onProgress) onProgress(i);
      return runNext();
    });
  }

  for (let j = 0; j < Math.min(limit, tasks.length); j++) {
    queue.push(runNext());
  }
  await Promise.all(queue);
  return results;
}

const DownloadManagerModal: React.FC<DownloadManagerModalProps> = ({
  isOpen,
  onClose,
  files: inputFiles,
  zipName = 'download',
  deliveryContext,
}) => {
  const firebase = useContext(FirebaseContext);
  const { currentOrgId } = useOrganizationStore();

  // File list with custom names
  const [files, setFiles] = useState<DownloadFile[]>([]);

  // Batch controls
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');

  // Format controls
  const [downloadingSingleId, setDownloadingSingleId] = useState<string | null>(null);
  // Session-scoped set of trackIds the user has downloaded individually
  // in this modal. Sent CUMULATIVELY to stampDeliveryDownload so the CF
  // can accurately compute per-delivery progress even when the user
  // grabs files one at a time (e.g. only the newly-added spot in a
  // partial redeliver). The CF uses Math.max on the existing stored
  // count so we never downgrade an already-recorded higher count.
  const [downloadedFileIds, setDownloadedFileIds] = useState<Set<string>>(() => new Set());
  // Reset the cumulative set when the modal opens with a fresh files
  // list — a new open should count from zero for this session.
  useEffect(() => {
    if (isOpen) setDownloadedFileIds(new Set());
  }, [isOpen]);
  const [format, setFormat] = useState('original');
  const [bitrate, setBitrate] = useState('192k');
  const [sampleRate, setSampleRate] = useState('');
  const [bitDepth, setBitDepth] = useState('');
  const [channels, setChannels] = useState('');
  const [timeStretchEnabled, setTimeStretchEnabled] = useState(false);
  const [timeStretchGraceMs, setTimeStretchGraceMs] = useState<number>(DEFAULT_GRACE_MS);

  // Per-batch stretch summary — shown after the batch completes so the user
  // knows which files were timed-stretched and which were within tolerance.
  const [stretchSummary, setStretchSummary] = useState<{
    stretched: { name: string; from: number; to: number }[];
    untouched: number;
    skipped: number;
  } | null>(null);

  // Presets
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // FTP send state. `ftpEnabled` reflects whether the current org has an
  // active FTP config — when true, the "Send to FTP" buttons render.
  // `sendingToFtp` gates the batch send button; `sendingFtpSingleId` gates
  // individual per-row buttons. `ftpSummary` shows the per-file result after
  // a batch completes (same shape pattern as `stretchSummary`).
  const [ftpEnabled, setFtpEnabled] = useState(false);
  const [sendingToFtp, setSendingToFtp] = useState(false);
  const [sendingFtpSingleId, setSendingFtpSingleId] = useState<string | null>(null);
  const [ftpSummary, setFtpSummary] = useState<{
    sent: { name: string }[];
    failed: { name: string; error?: string }[];
  } | null>(null);

  // Initialize files when modal opens
  useEffect(() => {
    if (isOpen && inputFiles.length > 0) {
      setFiles(inputFiles.map(f => ({
        ...f,
        customName: f.customName || f.originalName.replace(/\.[^.]+$/, ''),
      })));
    }
  }, [isOpen, inputFiles]);

  // Load presets
  useEffect(() => {
    if (isOpen && currentOrgId && firebase) {
      firebase.getDownloadPresets(currentOrgId).then((loaded: Preset[]) => {
        setPresets(loaded);
      });
    }
  }, [isOpen, currentOrgId, firebase]);

  // Check whether the current org has FTP enabled — drives whether the
  // "Send to FTP" buttons render. Reads /organizations/{orgId}.ftpEnabled
  // (the public flag, set true when the org first saves an FTP config and
  // back to false when FTP is disabled).
  useEffect(() => {
    if (!isOpen || !currentOrgId || !firebase) {
      setFtpEnabled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await fbGetDoc(fbDoc(firebase.db, 'organizations', currentOrgId));
        if (cancelled) return;
        setFtpEnabled(snap.exists() && (snap.data() as any)?.ftpEnabled === true);
      } catch {
        if (!cancelled) setFtpEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, currentOrgId, firebase]);

  /** Parse the expected spot duration from a file's metadata.
   *  Tries the spot/track name first (most reliable — that's the flighting
   *  code, e.g. "SHMA_30_248_E"), then falls back to the original filename
   *  with its extension stripped. Returns seconds, or null if no match. */
  const targetDurationFor = (file: DownloadFile): number | null => {
    const candidates: (string | undefined)[] = [
      file.trackName,
      file.customName,
      file.originalName?.replace(/\.[^.]+$/, ''),
    ];
    for (const c of candidates) {
      if (!c) continue;
      const parsed = parseFlightingCode(c);
      if (parsed?.duration) {
        const n = parseInt(parsed.duration.replace(/[^\d]/g, ''), 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return null;
  };

  const handleCustomNameChange = (index: number, value: string) => {
    setFiles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], customName: value };
      return updated;
    });
  };

  // Apply prefix/suffix to all files
  const handleApplyBatch = () => {
    setFiles(prev => prev.map(f => {
      const baseName = f.originalName.replace(/\.[^.]+$/, '');
      return { ...f, customName: `${prefix}${baseName}${suffix}` };
    }));
  };

  // Get the final filename with extension for a file
  const getFinalFilename = (file: DownloadFile): string => {
    const name = file.customName || file.originalName.replace(/\.[^.]+$/, '');
    if (format === 'original') {
      const ext = file.originalName.match(/\.[^.]+$/)?.[0] || `.${file.fileType}`;
      return `${name}${ext}`;
    }
    return `${name}.${format}`;
  };

  // Load a preset
  const handleLoadPreset = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;
    setSelectedPresetId(presetId);
    setPrefix(preset.prefix || '');
    setSuffix(preset.suffix || '');
    setFormat(preset.format || 'original');
    setBitrate(preset.bitrate || '192k');
    setSampleRate(preset.sampleRate || '');
    setBitDepth(preset.bitDepth || '');
    setChannels(preset.channels || '');
    setTimeStretchEnabled(preset.timeStretchEnabled === true);
    setTimeStretchGraceMs(
      typeof preset.timeStretchGraceMs === 'number' ? preset.timeStretchGraceMs : DEFAULT_GRACE_MS,
    );
    // Re-apply prefix/suffix
    setFiles(prev => prev.map(f => {
      const baseName = f.originalName.replace(/\.[^.]+$/, '');
      return { ...f, customName: `${preset.prefix || ''}${baseName}${preset.suffix || ''}` };
    }));
  };

  // Save current settings as preset
  const handleSavePreset = async () => {
    if (!currentOrgId || !firebase || !presetName.trim()) return;
    setSavingPreset(true);
    try {
      const presetData = {
        name: presetName.trim(),
        prefix,
        suffix,
        format,
        bitrate,
        sampleRate,
        bitDepth,
        channels,
        timeStretchEnabled,
        timeStretchGraceMs,
      };
      const id = await firebase.saveDownloadPreset(currentOrgId, presetData, selectedPresetId || undefined);
      // Reload presets
      const loaded = await firebase.getDownloadPresets(currentOrgId);
      setPresets(loaded);
      setSelectedPresetId(id);
      setPresetName('');
    } catch (err) {
      console.error('Error saving preset:', err);
    }
    setSavingPreset(false);
  };

  // Delete a preset
  const handleDeletePreset = async (presetId: string) => {
    if (!currentOrgId || !firebase) return;
    await firebase.deleteDownloadPreset(currentOrgId, presetId);
    setPresets(prev => prev.filter(p => p.id !== presetId));
    if (selectedPresetId === presetId) setSelectedPresetId('');
  };

  /** Fetch + (optionally) transcode + (optionally) time-stretch a single file.
   *  Mirrors the conversion pipeline used by handleDownload/handleDownloadSingle
   *  but returns the resulting Blob + final filename without writing anywhere.
   *  Used by both the ZIP download path and the new "Send to FTP" path so
   *  format/bitrate/sample-rate/channels/customName/time-stretch settings apply
   *  identically. */
  const prepareFileBlob = async (file: DownloadFile): Promise<{ fileName: string; blob: Blob } | null> => {
    const stretchActive = timeStretchEnabled;
    const needsConversion = format !== 'original' || stretchActive;
    try {
      if (!needsConversion) {
        const response = await fetch(file.downloadUrl);
        const blob = await response.blob();
        return { fileName: getFinalFilename(file), blob };
      }
      const originalResponse = await fetch(file.downloadUrl);
      const originalBlob = await originalResponse.blob();
      const target = stretchActive ? targetDurationFor(file) : null;
      const effectiveFormat = format === 'original' ? file.fileType : format;

      const formData = new FormData();
      formData.append('file', originalBlob, file.originalName);
      formData.append('format', effectiveFormat);
      formData.append('bitrate', bitrate);
      if (sampleRate) formData.append('sample_rate', sampleRate);
      if (bitDepth) formData.append('bit_depth', bitDepth);
      if (channels) formData.append('channels', channels);
      formData.append('filename', file.customName || file.originalName.replace(/\.[^.]+$/, ''));
      if (target !== null) {
        formData.append('target_duration_seconds', String(target));
        formData.append('grace_ms', String(timeStretchGraceMs));
      }
      const convertResponse = await fetch(`${QC_SERVICE_URL}/convert`, { method: 'POST', body: formData });
      if (!convertResponse.ok) {
        console.warn(`Conversion failed for ${file.originalName}:`, await convertResponse.text());
        return null;
      }
      const convertedBlob = await convertResponse.blob();
      return { fileName: getFinalFilename(file), blob: convertedBlob };
    } catch (err) {
      console.warn(`prepareFileBlob failed for ${file.originalName}:`, err);
      return null;
    }
  };

  /** Upload a blob to /ftp-staging/{orgId}/... so the Cloud Function can
   *  read it. Returns the storage path. Caller is responsible for handing
   *  this path to `sendFilesToOwnFtp`, which deletes the staging blob
   *  after a successful FTP push. */
  const uploadToStaging = async (orgId: string, fileName: string, blob: Blob): Promise<string> => {
    if (!firebase) throw new Error('Firebase not ready');
    const safeName = fileName.replace(/[^A-Za-z0-9._-]/g, '_');
    const unique = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const path = `ftp-staging/${orgId}/${unique}_${safeName}`;
    await uploadBytes(storageRef(firebase.storage, path), blob, { contentType: blob.type || undefined });
    return path;
  };

  /** Per-row "Send to FTP" handler. Converts the file, uploads to staging,
   *  then calls sendFilesToOwnFtp for a single-file send. */
  const handleSendSingleToFtp = async (file: DownloadFile) => {
    if (!firebase || !currentOrgId) return;
    setSendingFtpSingleId(file.id);
    try {
      const prepared = await prepareFileBlob(file);
      if (!prepared) { console.error('FTP send: file prep failed'); return; }
      const storagePath = await uploadToStaging(currentOrgId, prepared.fileName, prepared.blob);
      const result: any = await firebase.callFunction('sendFilesToOwnFtp', {
        orgId: currentOrgId,
        files: [{ storagePath, remoteName: prepared.fileName }],
      });
      const data = result?.data || result;
      if (data?.success) {
        setFtpSummary({ sent: [{ name: prepared.fileName }], failed: [] });
        // Stamp the source's delivery record — per-file mode. Same
        // cumulative-trackId pattern as the single-file zip handler
        // so progressive FTP-forwards accurately move the counter.
        if (deliveryContext && file.id) {
          const nextIds = new Set(downloadedFileIds);
          nextIds.add(file.id);
          setDownloadedFileIds(nextIds);
          try {
            await firebase.callFunction('stampDeliveryDownload', {
              sourceOrgId: deliveryContext.sourceOrgId,
              jobId: deliveryContext.jobId,
              broadcasterOrgId: deliveryContext.broadcasterOrgId,
              downloadMethod: 'ftp',
              trackIds: Array.from(nextIds),
            });
          } catch (err) {
            console.warn('[DownloadManagerModal] stampDeliveryDownload (single ftp) failed (best effort):', err);
          }
        }
      } else {
        const errMsg = (data?.results?.[0]?.error) || data?.error || 'FTP send failed';
        setFtpSummary({ sent: [], failed: [{ name: prepared.fileName, error: errMsg }] });
      }
    } catch (err: any) {
      console.error('FTP send (single) failed:', err);
      setFtpSummary({ sent: [], failed: [{ name: file.customName || file.originalName, error: err?.message || 'FTP send failed' }] });
    } finally {
      setSendingFtpSingleId(null);
    }
  };

  /** Batch "Send all to FTP" handler. Same pipeline as ZIP download — runs
   *  prepareFileBlob with the configured concurrency, uploads each result
   *  to staging, then makes a single bulk callable to push them all.
   *  Falls back to per-file summary on partial failure. */
  const handleSendAllToFtp = async () => {
    if (!firebase || !currentOrgId || files.length === 0) return;
    setSendingToFtp(true);
    setFtpSummary(null);
    setStretchSummary(null);
    setProgress({ current: 0, total: files.length });

    type Staged = { storagePath: string; remoteName: string };
    const staged: Staged[] = [];
    const prepFailures: { name: string; error?: string }[] = [];

    const tasks = files.map((file) => async () => {
      try {
        const prepared = await prepareFileBlob(file);
        if (!prepared) {
          prepFailures.push({ name: file.customName || file.originalName, error: 'Conversion failed' });
          return null;
        }
        const storagePath = await uploadToStaging(currentOrgId, prepared.fileName, prepared.blob);
        return { storagePath, remoteName: prepared.fileName };
      } catch (err: any) {
        prepFailures.push({ name: file.customName || file.originalName, error: err?.message || 'Upload to staging failed' });
        return null;
      }
    });

    const onProgress = (done: number) => setProgress((p) => ({ ...p, current: done }));
    const results = await fetchWithConcurrencyLimit(tasks, CONCURRENCY_LIMIT, onProgress);
    for (const r of results) if (r) staged.push(r as Staged);

    if (staged.length === 0) {
      setFtpSummary({ sent: [], failed: prepFailures });
      setSendingToFtp(false);
      setProgress({ current: 0, total: 0 });
      return;
    }

    try {
      const result: any = await firebase.callFunction('sendFilesToOwnFtp', {
        orgId: currentOrgId,
        files: staged,
      });
      const data = result?.data || result;
      const sent: { name: string }[] = [];
      const failed: { name: string; error?: string }[] = [...prepFailures];
      const perFile = Array.isArray(data?.results) ? data.results : [];
      for (const r of perFile) {
        const name = r.remoteName || (r.file || '').split('/').pop() || '';
        if (r.success) sent.push({ name });
        else failed.push({ name, error: r.error || 'FTP push failed' });
      }
      setFtpSummary({ sent, failed });

      // Stamp the source's delivery records — same treatment as the
      // ZIP download path. Method is 'ftp' so the studio's per-delivery
      // badge can indicate how the broadcaster consumed the delivery.
      if (deliveryContext) {
        try {
          await firebase.callFunction('stampDeliveryDownload', {
            sourceOrgId: deliveryContext.sourceOrgId,
            jobId: deliveryContext.jobId,
            broadcasterOrgId: deliveryContext.broadcasterOrgId,
            downloadMethod: 'ftp',
          });
        } catch (err) {
          console.warn('[DownloadManagerModal] stampDeliveryDownload (batch ftp) failed (best effort):', err);
        }
      }
    } catch (err: any) {
      console.error('FTP send (batch) failed:', err);
      setFtpSummary({ sent: [], failed: [...prepFailures, ...staged.map((s) => ({ name: s.remoteName, error: err?.message || 'FTP send failed' }))] });
    } finally {
      setSendingToFtp(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  // Download all files as zip
  const handleDownload = async () => {
    if (files.length === 0) return;

    setDownloading(true);
    setStretchSummary(null);
    setProgress({ current: 0, total: files.length });

    const zip = new JSZip();
    // We route through /convert whenever the user picks a non-original format
    // OR when they ask for time-stretching (atempo only runs through the
    // backend). When stretching with format=original we re-encode in the
    // source file's own format so the container/codec doesn't change.
    const stretchActive = timeStretchEnabled;
    const needsConversion = format !== 'original' || stretchActive;
    const stretched: { name: string; from: number; to: number }[] = [];
    let untouched = 0;
    let skipped = 0;

    const fetchTasks = files.map((file) => {
      return async () => {
        try {
          if (needsConversion) {
            // Fetch original file, then convert via backend
            const originalResponse = await fetch(file.downloadUrl);
            const originalBlob = await originalResponse.blob();

            const target = stretchActive ? targetDurationFor(file) : null;
            // When format is 'original' and we're only routing through the
            // backend for time-stretching, use the source file's own type so
            // the output container/codec match the input.
            const effectiveFormat = format === 'original' ? file.fileType : format;

            const formData = new FormData();
            formData.append('file', originalBlob, file.originalName);
            formData.append('format', effectiveFormat);
            formData.append('bitrate', bitrate);
            if (sampleRate) formData.append('sample_rate', sampleRate);
            if (bitDepth) formData.append('bit_depth', bitDepth);
            if (channels) formData.append('channels', channels);
            formData.append('filename', file.customName || file.originalName.replace(/\.[^.]+$/, ''));
            if (target !== null) {
              formData.append('target_duration_seconds', String(target));
              formData.append('grace_ms', String(timeStretchGraceMs));
            } else if (stretchActive) {
              // Couldn't infer a target — leave the file untouched and log
              // it in the per-batch summary so the user knows.
              skipped++;
            }

            const convertResponse = await fetch(`${QC_SERVICE_URL}/convert`, {
              method: 'POST',
              body: formData,
            });

            if (!convertResponse.ok) {
              console.warn(`Conversion failed for ${file.originalName}:`, await convertResponse.text());
              return null;
            }

            if (stretchActive && target !== null) {
              if (convertResponse.headers.get('X-Stretched') === '1') {
                stretched.push({
                  name: getFinalFilename(file),
                  from: parseFloat(convertResponse.headers.get('X-Source-Duration') || '0'),
                  to: target,
                });
              } else {
                untouched++;
              }
            }

            const convertedBlob = await convertResponse.blob();
            return { fileName: getFinalFilename(file), blob: convertedBlob };
          } else {
            // Direct download, no conversion
            const response = await fetch(file.downloadUrl);
            const blob = await response.blob();
            return { fileName: getFinalFilename(file), blob };
          }
        } catch (err) {
          console.warn(`Failed to download ${file.originalName}:`, err);
          return null;
        }
      };
    });

    const onProgress = (done: number) => setProgress(p => ({ ...p, current: done }));
    const results = await fetchWithConcurrencyLimit(fetchTasks, CONCURRENCY_LIMIT, onProgress);

    // Add to zip
    results.forEach((result: any) => {
      if (result) {
        zip.file(result.fileName, result.blob);
      }
    });

    // Generate and download zip
    const sanitizedName = zipName.replace(/[^a-zA-Z0-9 _-]/g, '_');
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizedName}.zip`;
    a.click();
    URL.revokeObjectURL(url);

    // Stamp the source's delivery records so the studio sees "downloaded"
    // in their Jobs list + JobDetail per-delivery badge. Only fires when
    // the modal was opened with deliveryContext (broadcaster path).
    // Batch mode — no trackIds passed = CF stamps every unstamped
    // delivery as complete (per-delivery file counts computed inside
    // the CF, so we don't need to pass fileCount/totalCount from here).
    if (deliveryContext && firebase) {
      try {
        await firebase.callFunction('stampDeliveryDownload', {
          sourceOrgId: deliveryContext.sourceOrgId,
          jobId: deliveryContext.jobId,
          broadcasterOrgId: deliveryContext.broadcasterOrgId,
          downloadMethod: 'zip',
        });
      } catch (err) {
        console.warn('[DownloadManagerModal] stampDeliveryDownload (batch zip) failed (best effort):', err);
      }
    }

    setDownloading(false);
    setProgress({ current: 0, total: 0 });
    if (stretchActive) {
      setStretchSummary({ stretched, untouched, skipped });
    }
  };

  // Single file download (no zip)
  const handleDownloadSingle = async (file: DownloadFile) => {
    setDownloadingSingleId(file.id);
    // Track whether the download actually succeeded so we can stamp
    // the source's delivery record afterward. Both branches (convert
    // and direct) set this true only when their fetch + blob path
    // completes without throwing.
    let downloadSucceeded = false;
    try {
    // Route through /convert whenever a format was explicitly chosen OR when
    // time-stretching is on (atempo only runs server-side). For 'original'+
    // stretch, we re-encode in the source file's own format.
    if (format !== 'original' || timeStretchEnabled) {
      // Convert via backend
      try {
        const originalResponse = await fetch(file.downloadUrl);
        const originalBlob = await originalResponse.blob();

        const target = timeStretchEnabled ? targetDurationFor(file) : null;
        const effectiveFormat = format === 'original' ? file.fileType : format;

        const formData = new FormData();
        formData.append('file', originalBlob, file.originalName);
        formData.append('format', effectiveFormat);
        formData.append('bitrate', bitrate);
        if (sampleRate) formData.append('sample_rate', sampleRate);
        if (bitDepth) formData.append('bit_depth', bitDepth);
        if (channels) formData.append('channels', channels);
        formData.append('filename', file.customName || file.originalName.replace(/\.[^.]+$/, ''));
        if (target !== null) {
          formData.append('target_duration_seconds', String(target));
          formData.append('grace_ms', String(timeStretchGraceMs));
        }

        const convertResponse = await fetch(`${QC_SERVICE_URL}/convert`, {
          method: 'POST',
          body: formData,
        });

        if (!convertResponse.ok) {
          console.error('Conversion failed');
          return;
        }

        if (timeStretchEnabled && target !== null) {
          if (convertResponse.headers.get('X-Stretched') === '1') {
            setStretchSummary({
              stretched: [{
                name: getFinalFilename(file),
                from: parseFloat(convertResponse.headers.get('X-Source-Duration') || '0'),
                to: target,
              }],
              untouched: 0,
              skipped: 0,
            });
          } else {
            setStretchSummary({ stretched: [], untouched: 1, skipped: 0 });
          }
        }

        const blob = await convertResponse.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = getFinalFilename(file);
        a.click();
        URL.revokeObjectURL(url);
        downloadSucceeded = true;
      } catch (err) {
        console.error('Download failed:', err);
      }
    } else {
      // Direct download — fetch blob to force download (Firebase URLs don't support download attribute)
      try {
        const response = await fetch(file.downloadUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = getFinalFilename(file);
        a.click();
        URL.revokeObjectURL(url);
        downloadSucceeded = true;
      } catch (err) {
        console.error('Download failed:', err);
      }
    }
    // Stamp the source's delivery record with the cumulative trackId
    // set. CF uses the trackIds to figure out which delivery this
    // download belongs to and whether it should be marked complete or
    // partial. Only fires when the modal was opened with
    // deliveryContext (broadcaster path) AND the download actually
    // succeeded. Best-effort — never blocks the download.
    if (downloadSucceeded && deliveryContext && firebase && file.id) {
      const nextIds = new Set(downloadedFileIds);
      nextIds.add(file.id);
      setDownloadedFileIds(nextIds);
      try {
        await firebase.callFunction('stampDeliveryDownload', {
          sourceOrgId: deliveryContext.sourceOrgId,
          jobId: deliveryContext.jobId,
          broadcasterOrgId: deliveryContext.broadcasterOrgId,
          downloadMethod: 'zip',
          trackIds: Array.from(nextIds),
        });
      } catch (err) {
        console.warn('[DownloadManagerModal] stampDeliveryDownload (single) failed (best effort):', err);
      }
    }
    } finally {
      setDownloadingSingleId(null);
    }
  };

  const isLossy = format === 'mp3' || format === 'mp2' || format === 'aac' || format === 'ogg';
  const isLossless = format === 'wav' || format === 'flac';
  const BITRATE_OPTIONS = format === 'mp2' ? BITRATE_OPTIONS_MP2 : BITRATE_OPTIONS_DEFAULT;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      style={{ maxWidth: '900px', margin: '1.75rem auto' }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={onClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button className="close" onClick={onClose} style={{
            color: 'var(--accent-1-color)', opacity: 1, textShadow: 'none',
            background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
          }}>
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          <i className="tim-icons icon-cloud-download-93 mr-2" style={{ color: 'var(--accent-1-color)' }}></i>
          Download Manager — {files.length} file{files.length !== 1 ? 's' : ''}
        </span>
      </ModalHeader>

      <ModalBody style={{
        backgroundColor: 'var(--chatbot-color-1)',
        color: '#ffffff',
        padding: '1.5rem',
        maxHeight: '70vh',
        overflowY: 'auto',
      }}>
        {/* Preset & Batch Controls */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem',
          marginBottom: '1.5rem', padding: '1rem',
          backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          {/* Left: Batch naming */}
          <div>
            <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', display: 'block' }}>
              Batch File Naming
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <Input
                bsSize="sm"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="Prefix..."
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', flex: 1 }}
              />
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>filename</span>
              <Input
                bsSize="sm"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                placeholder="Suffix..."
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', flex: 1 }}
              />
            </div>
            <Button size="sm" className="btn-primary-custom" onClick={handleApplyBatch} style={{ fontSize: '0.75rem' }}>
              <span style={{ position: 'relative', zIndex: 2 }}>Apply to All</span>
            </Button>
          </div>

          {/* Right: Format & presets */}
          <div>
            <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', display: 'block' }}>
              Output Format
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <Input
                type="select" bsSize="sm" value={format}
                onChange={(e) => setFormat(e.target.value)}
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', flex: 1, maxWidth: '180px' }}
              >
                {FORMAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Input>
              {isLossy && (
                <Input
                  type="select" bsSize="sm" value={bitrate}
                  onChange={(e) => setBitrate(e.target.value)}
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', maxWidth: '100px' }}
                >
                  {BITRATE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                </Input>
              )}
              {isLossless && (
                <Input
                  type="select" bsSize="sm" value={bitDepth}
                  onChange={(e) => setBitDepth(e.target.value)}
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', maxWidth: '100px' }}
                >
                  <option value="">Bit Depth</option>
                  {BIT_DEPTH_OPTIONS.filter(b => b).map(b => <option key={b} value={b}>{b}-bit</option>)}
                </Input>
              )}
              <Input
                type="select" bsSize="sm" value={sampleRate}
                onChange={(e) => setSampleRate(e.target.value)}
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', maxWidth: '110px' }}
              >
                <option value="">Sample Rate</option>
                {SAMPLE_RATE_OPTIONS.filter(s => s).map(s => <option key={s} value={s}>{parseInt(s)/1000}kHz</option>)}
              </Input>
              <Input
                type="select" bsSize="sm" value={channels}
                onChange={(e) => setChannels(e.target.value)}
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem', maxWidth: '100px' }}
              >
                <option value="">Channels</option>
                <option value="1">Mono</option>
                <option value="2">Stereo</option>
              </Input>
            </div>

            {/* Time-stretch controls. Works with any output format, including
                'Original' — when format is Original we route via /convert in
                the source file's own format so atempo can still be applied. */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '0.75rem', cursor: 'pointer', margin: 0,
                }}
                title="Stretch each file to the duration encoded in its name (e.g. _30_ → 30s)"
              >
                <input
                  type="checkbox"
                  checked={timeStretchEnabled}
                  onChange={(e) => setTimeStretchEnabled(e.target.checked)}
                  style={{ accentColor: 'var(--accent-1-color)' }}
                />
                Stretch to expected duration
              </label>
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem' }}>± tolerance</span>
              <Input
                bsSize="sm"
                type="number"
                min={0}
                max={5000}
                value={timeStretchGraceMs}
                onChange={(e) => setTimeStretchGraceMs(Math.max(0, parseInt(e.target.value || '0', 10)))}
                disabled={!timeStretchEnabled}
                style={{
                  backgroundColor: 'rgba(255,255,255,0.08)', border: 'none',
                  color: '#fff', fontSize: '0.75rem', width: '70px',
                }}
              />
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>ms</span>
            </div>

            {/* Preset controls */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Input
                type="select" bsSize="sm" value={selectedPresetId}
                onChange={(e) => { if (e.target.value) handleLoadPreset(e.target.value); else setSelectedPresetId(''); }}
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.75rem', flex: 1 }}
              >
                <option value="">Load Preset...</option>
                {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Input>
              {selectedPresetId && (
                <button
                  onClick={() => handleDeletePreset(selectedPresetId)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '0.9rem', padding: '0 4px' }}
                  title="Delete preset"
                >
                  &times;
                </button>
              )}
              <Input
                bsSize="sm"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Preset name..."
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.75rem', flex: 1 }}
              />
              <Button
                size="sm" className="btn-info-custom"
                onClick={handleSavePreset}
                disabled={!presetName.trim() || savingPreset}
                style={{ fontSize: '0.7rem', padding: '2px 8px', whiteSpace: 'nowrap' }}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  {savingPreset ? <Spinner size="sm" /> : 'Save'}
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* File list */}
        <Table dark responsive size="sm" style={{ backgroundColor: 'transparent', marginBottom: 0 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <th style={{ border: 'none', padding: '0.5rem', width: '40%' }}>Custom File Name</th>
              <th style={{ border: 'none', padding: '0.5rem' }}>Original</th>
              <th style={{ border: 'none', padding: '0.5rem' }}>Type</th>
              <th style={{ border: 'none', padding: '0.5rem' }}>Output</th>
              <th style={{ border: 'none', padding: '0.5rem', width: '30px' }}></th>
            </tr>
          </thead>
          <tbody>
            {files.map((file, idx) => (
              <tr key={file.id || idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <td style={{ border: 'none', padding: '0.25rem' }}>
                  <Input
                    bsSize="sm"
                    value={file.customName}
                    onChange={(e) => handleCustomNameChange(idx, e.target.value)}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.08)', border: 'none',
                      color: 'var(--accent-1-color)', fontSize: '0.8rem', fontFamily: 'monospace',
                    }}
                  />
                </td>
                <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                  {file.originalName}
                </td>
                <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  {file.fileType}
                </td>
                <td style={{ border: 'none', padding: '0.5rem', fontSize: '0.75rem' }}>
                  <span style={{ color: format === 'original' ? 'rgba(255,255,255,0.4)' : '#50c878', fontFamily: 'monospace' }}>
                    {getFinalFilename(file)}
                  </span>
                </td>
                <td style={{ border: 'none', padding: '0.25rem' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => handleDownloadSingle(file)}
                      disabled={downloadingSingleId === file.id}
                      style={{
                        background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                        color: downloadingSingleId === file.id ? 'var(--color-accent)' : 'rgba(255,255,255,0.5)',
                        cursor: downloadingSingleId === file.id ? 'wait' : 'pointer',
                        fontSize: '0.7rem', padding: '2px 6px', borderRadius: '3px',
                      }}
                      title="Download this file"
                    >
                      {downloadingSingleId === file.id
                        ? <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                        : <i className="tim-icons icon-cloud-download-93"></i>}
                    </button>
                    {ftpEnabled && (
                      <button
                        onClick={() => handleSendSingleToFtp(file)}
                        disabled={sendingFtpSingleId === file.id || sendingToFtp}
                        style={{
                          background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                          color: sendingFtpSingleId === file.id ? 'var(--color-accent)' : 'rgba(255,255,255,0.5)',
                          cursor: sendingFtpSingleId === file.id ? 'wait' : 'pointer',
                          fontSize: '0.7rem', padding: '2px 6px', borderRadius: '3px',
                        }}
                        title="Send this file to your FTP"
                      >
                        {sendingFtpSingleId === file.id
                          ? <Spinner size="sm" style={{ width: '12px', height: '12px' }} />
                          : <i className="tim-icons icon-send"></i>}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Time-stretch result summary — shown after a batch finishes when
            the stretch toggle was active. Lists each file that was actually
            stretched (with from → to durations) and the counts for files that
            were already within tolerance or skipped because no target was
            inferable from the name. */}
        {stretchSummary && !downloading && (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem',
            backgroundColor: 'rgba(107,107,255,0.08)',
            border: '1px solid rgba(107,107,255,0.25)',
            borderRadius: '6px', fontSize: '0.78rem',
          }}>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: '0.35rem' }}>
              Time-stretch summary
            </div>
            {stretchSummary.stretched.length > 0 ? (
              <>
                <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.35rem' }}>
                  {stretchSummary.stretched.length} file
                  {stretchSummary.stretched.length === 1 ? '' : 's'} stretched to target:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'rgba(255,255,255,0.85)' }}>
                  {stretchSummary.stretched.map((s) => (
                    <li key={s.name} style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                      {s.name} — {s.from.toFixed(3)}s → {s.to}s
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>No files needed stretching.</div>
            )}
            {(stretchSummary.untouched > 0 || stretchSummary.skipped > 0) && (
              <div style={{ marginTop: '0.5rem', color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem' }}>
                {stretchSummary.untouched > 0 && `${stretchSummary.untouched} within tolerance`}
                {stretchSummary.untouched > 0 && stretchSummary.skipped > 0 && ' · '}
                {stretchSummary.skipped > 0 && `${stretchSummary.skipped} skipped (no duration in filename)`}
              </div>
            )}
          </div>
        )}

        {/* Download progress */}
        {downloading && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                {format !== 'original' ? 'Converting & downloading...' : 'Downloading...'}
              </span>
              <span style={{ color: 'var(--accent-1-color)', fontSize: '0.8rem' }}>
                {progress.current}/{progress.total}
              </span>
            </div>
            <Progress
              value={(progress.current / progress.total) * 100}
              style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)' }}
              barStyle={{ backgroundColor: 'var(--accent-1-color)' }}
            />
          </div>
        )}

        {/* FTP send progress + summary */}
        {sendingToFtp && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>Preparing & uploading to FTP...</span>
              <span style={{ color: 'var(--accent-1-color)', fontSize: '0.8rem' }}>{progress.current}/{progress.total}</span>
            </div>
            <Progress
              value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
              style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)' }}
              barStyle={{ backgroundColor: 'var(--accent-1-color)' }}
            />
          </div>
        )}
        {ftpSummary && !sendingToFtp && (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem',
            backgroundColor: 'rgba(107,107,255,0.08)',
            border: '1px solid rgba(107,107,255,0.25)',
            borderRadius: '6px', fontSize: '0.78rem',
          }}>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: '0.35rem' }}>FTP send summary</div>
            {ftpSummary.sent.length > 0 && (
              <>
                <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '0.35rem' }}>
                  {ftpSummary.sent.length} file{ftpSummary.sent.length === 1 ? '' : 's'} sent:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'rgba(255,255,255,0.85)' }}>
                  {ftpSummary.sent.map((s) => (
                    <li key={s.name} style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{s.name}</li>
                  ))}
                </ul>
              </>
            )}
            {ftpSummary.failed.length > 0 && (
              <>
                <div style={{ color: '#ff9b9b', marginTop: '0.5rem', marginBottom: '0.25rem' }}>
                  {ftpSummary.failed.length} failed:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'rgba(255,200,200,0.85)' }}>
                  {ftpSummary.failed.map((f, i) => (
                    <li key={i} style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{f.name}{f.error ? ` — ${f.error}` : ''}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{
        backgroundColor: 'var(--chatbot-color-2)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
      }}>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
          {format !== 'original' && (
            <span>Converting to <strong style={{ color: 'var(--accent-1-color)' }}>{format.toUpperCase()}</strong>
              {isLossy && ` @ ${bitrate}`}
              {sampleRate && ` · ${parseInt(sampleRate)/1000}kHz`}
              {timeStretchEnabled && ` · stretch ±${timeStretchGraceMs}ms`}
            </span>
          )}
          {format === 'original' && timeStretchEnabled && (
            <span>Original format · <strong style={{ color: 'var(--accent-1-color)' }}>stretch ±{timeStretchGraceMs}ms</strong></span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button className="btn-info-custom" onClick={onClose} size="sm">
            <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
          </Button>
          {ftpEnabled && (
            <Button
              className="btn-info-custom"
              onClick={handleSendAllToFtp}
              disabled={sendingToFtp || downloading || files.length === 0}
              size="sm"
              title="Send all files to your organization's FTP"
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                {sendingToFtp ? (
                  <><Spinner size="sm" className="mr-1" /> Sending...</>
                ) : (
                  <><i className="tim-icons icon-send mr-1"></i>Send All to FTP</>
                )}
              </span>
            </Button>
          )}
          <Button
            className="btn-success-custom"
            onClick={handleDownload}
            disabled={downloading || sendingToFtp || files.length === 0}
            size="sm"
          >
            <span style={{ position: 'relative', zIndex: 2 }}>
              {downloading ? (
                <><Spinner size="sm" className="mr-1" /> Downloading...</>
              ) : (
                <><i className="tim-icons icon-cloud-download-93 mr-1"></i>Download All as ZIP</>
              )}
            </span>
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default DownloadManagerModal;
